import React, { useMemo } from "react";
import { PropTypes } from "prop-types";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Forum, Videocam, PlayCircleFilled } from "@mui/icons-material";
import { groupBy } from "lodash";
import Table from "../../../../components/ReactTable/Table";
import { formatDate } from "../../../../utils/datetime";
import { useZoomMeetingRecordings } from "../../../../hooks/zoomMeetings";
import { useSessionsRecordings } from "../../../../hooks/sessionsRecordings";
import { useTableData } from "../../../../components/ReactTable/useTableData";

const zoomRecordingTypes = {
  video: "MP4",
  chat: "CHAT"
};

const getRecordingsTableData = sessionsRecordings => {
  const tableData = [];
  sessionsRecordings &&
    sessionsRecordings.forEach(sessionsRecording => {
      if (
        sessionsRecording.zoomMeetingRecordings &&
        sessionsRecording.zoomMeetingRecordings.data.length > 0
      ) {
        const zoomMeetingRecordings = Object.values(
          groupBy(
            sessionsRecording.zoomMeetingRecordings.data,
            "recordingStart"
          )
        );
        const tableRows = zoomMeetingRecordings.map(
          (recordingGroup, index) => ({
            sessionName: sessionsRecording.name,
            instructor: sessionsRecording.instructor,
            recordings: recordingGroup,
            dateRecorded: new Date(recordingGroup[0].attributes.recordingStart),
            zoomMeetingId: sessionsRecording.zoomMeetingId,
            id: recordingGroup[0].attributes.id
          })
        );
        tableData.push(...tableRows);
      }
    });
  return tableData;
};

const hasRecording = (recordings, recordingType) => {
  const recording = recordings.find(
    ({ attributes }) => attributes.fileType === recordingType
  );
  return !!recording;
};

const DownloadButtons = ({ recordings, zoomMeetingId }) => {
  const { t } = useTranslation();
  const { getZoomMeetingRecordings } = useZoomMeetingRecordings();
  const hasChatRecording = hasRecording(recordings, zoomRecordingTypes.chat);
  const hasVideoRecording = hasRecording(recordings, zoomRecordingTypes.video);

  const handleDownload = recordingType => {
    getZoomMeetingRecordings(zoomMeetingId).then(recordings => {
      const recording = recordings.find(
        recording => recording.fileType === recordingType
      );
      if (recording) {
        window.location = recording.downloadUrl;
      }
    });
  };

  return (
    <Box display="flex" width={70} justifyContent="center">
      <Tooltip
        placement="top"
        title={
          hasVideoRecording
            ? t("courseRecordings.downloadVideoTooltip")
            : t("courseRecordings.noVideoRecordingTooltip")
        }
      >
        <div>
          <IconButton
            color="primary"
            onClick={() => handleDownload(zoomRecordingTypes.video)}
            disabled={!hasVideoRecording}
            size="large"
          >
            <Videocam />
          </IconButton>
        </div>
      </Tooltip>
      <Tooltip
        placement="top"
        title={
          hasChatRecording
            ? t("courseRecordings.downloadChatTooltip")
            : t("courseRecordings.noChatRecordingTooltip")
        }
      >
        <div>
          <IconButton
            color="primary"
            onClick={() => handleDownload(zoomRecordingTypes.chat)}
            disabled={!hasChatRecording}
            size="large"
          >
            <Forum />
          </IconButton>
        </div>
      </Tooltip>
    </Box>
  );
};

const ViewButton = ({ recordings }) => {
  const handlePlayRecording = () => {
    const videoRecording = recordings.find(
      recording => recording.attributes.fileType === zoomRecordingTypes.video
    );
    if (videoRecording) {
      window.open(videoRecording.attributes.playUrl);
    }
  };

  return (
    <IconButton
      color="primary"
      onClick={() => handlePlayRecording()}
      size="large"
    >
      <PlayCircleFilled />
    </IconButton>
  );
};

const columns = t => [
  {
    Header: t("courseRecordings.sessionColumnHeader"),
    accessor: "sessionName",
    canSort: true
  },
  {
    Header: t("courseRecordings.instructorColumnHeader"),
    accessor: "instructor",
    canSort: true
  },
  {
    Header: t("courseRecordings.dateColumnHeader"),
    accessor: "dateRecorded",
    Cell: ({ cell: { value } }) => formatDate(value),
    canSort: true,
    id: "dateRecorded",
    sortType: "datetime"
  },
  {
    Header: t("courseRecordings.viewColumnHeader"),
    accessor: row => <ViewButton recordings={row.recordings} />,
    disableSortBy: true,
    id: "dueDate"
  },
  {
    Header: t("courseRecordings.downloadColumnHeader"),
    accessor: row => (
      <DownloadButtons
        recordings={row.recordings}
        zoomMeetingId={row.zoomMeetingId}
      />
    ),
    disableSortBy: true,
    id: "comments"
  }
];

const CourseRecordings = ({ courseId }) => {
  const { fetchSessionsRecordings } = useSessionsRecordings({
    courseId,
    status: "all"
  });
  const { data, paginationState, isFetching } = useTableData(
    fetchSessionsRecordings
  );

  const { t } = useTranslation();
  const memoColumns = useMemo(() => columns(t), []);

  const recordingsTableData = useMemo(() => getRecordingsTableData(data), [
    data
  ]);

  return (
    <Box mt={4}>
      <Table
        manualSortBy={false}
        data={recordingsTableData}
        columns={memoColumns}
        loading={isFetching}
        paginated={true}
        paginationState={paginationState}
      />
    </Box>
  );
};

export default CourseRecordings;

CourseRecordings.propTypes = {
  courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
